/**
 * FX Sim Data
 *
 * KYC/企业信息模拟数据工具库
 * 为其他项目提供完整的TypeScript类型支持和faker.js驱动的数据支持
 *
 * @packageDocumentation
 */

/* ============================================================================
 * 个人KYC模块
 * ========================================================================== */

// 个人信息
export {
  generatePersonalInfoMockData as createPersonalInfo,
  generatePersonalInfoMockDataBatch as createPersonalInfoBatch,
  mockDataPresets as personalInfoPresets,
  type PersonalInfoFormData,
  type DocumentValidityDate,
  type OverseasResidentInfo,
  type MockDataGeneratorOptions,
} from "./personal/personal-info/mockDataGenerator.personal-info";

// 地址信息
export {
  generateAddressInfoMockData as createAddressInfo,
  generateAddressInfoMockDataBatch as createAddressInfoBatch,
  addressMockDataPresets as addressInfoPresets,
  RESIDENCE_PROOF_TYPES,
  type AddressInfoFormData,
  type AddressMockDataGeneratorOptions,
} from "./personal/address-info/mockDataGenerator.address-info";

// 财务信息
export {
  generateFinancialInfoMockData as createFinancialInfo,
  generateFinancialInfoMockDataBatch as createFinancialInfoBatch,
  financialMockDataPresets as financialInfoPresets,
  type FinancialInfoFormData,
  type TaxJurisdiction,
  type FinancialMockDataGeneratorOptions,
} from "./personal/financial-info/mockDataGenerator.financial-info";

/* ============================================================================
 * 企业KYC模块
 * ========================================================================== */

// 公司基本信息
import {
  generateCompanyInfoMockData,
  generateCompanyInfoMockDataBatch,
  companyMockDataPresets,
} from "./company/company-info/mockDataGenerator.company";

export const createCompanyInfo = generateCompanyInfoMockData;
export const createCompanyInfoBatch = generateCompanyInfoMockDataBatch;
export const companyInfoPresets = companyMockDataPresets;

export type {
  CompanyInfoFormData,
  CompanyMockDataGeneratorOptions as CompanyInfoOptions,
} from "./company/company-info/mockDataGenerator.company";

// 公司结构与关联人士
import {
  generateRelatedPersonData,
  generateRelatedPersonsBatch,
  companyStructurePresets,
} from "./company/company-structure/mockDataGenerator.company-structure";

export const createRelatedPerson = generateRelatedPersonData;
export const createRelatedPersonBatch = generateRelatedPersonsBatch;
export { companyStructurePresets };

export type {
  RelatedPersonData,
  CompanyStructureMockDataOptions as CompanyStructureOptions,
} from "./company/company-structure/mockDataGenerator.company-structure";

// 公司财务信息
import {
  generateCompanyFinancialMockData,
  generateCompanyFinancialMockDataBatch,
  companyFinancialPresets,
} from "./company/company-financial/mockDataGenerator.company-financial";

export const createCompanyFinancial = generateCompanyFinancialMockData;
export const createCompanyFinancialBatch =
  generateCompanyFinancialMockDataBatch;
export { companyFinancialPresets };

export type {
  CompanyFinancialInfoFormData,
  CompanyFinancialMockDataOptions as CompanyFinancialOptions,
} from "./company/company-financial/mockDataGenerator.company-financial";

/* ============================================================================
 * 测评与问卷模块
 * ========================================================================== */

// 风险测评
import {
  generateRiskAssessmentMockData,
  generateRandomRiskType,
  RISK_ASSESSMENT_PRESETS,
} from "./risk-assessment/mockDataGenerator";

export const createRiskAssessment = generateRiskAssessmentMockData;
export const getRandomRiskType = generateRandomRiskType;
export { RISK_ASSESSMENT_PRESETS };

export type { RiskAssessmentMockConfig as RiskAssessmentConfig } from "./risk-assessment/mockDataGenerator";

// 虚拟资产知识测试
import {
  generateVirtualAssetMockData,
  calculateVirtualAssetScore,
  generateRandomScore,
  VIRTUAL_ASSET_PRESETS,
} from "./virtual-asset-test/mockDataGenerator";
import {
  triggerAllFileUploads,
  triggerFileUpload,
  triggerFileUploadByTestId,
  triggerMultipleFileUploads,
} from "./utils/mockFileUploadTrigger";
import {
  generatePersonalInfoMockData,
  generatePersonalInfoMockDataBatch,
} from "./personal/personal-info/mockDataGenerator.personal-info";
import { personalInfoPresets } from ".";
import { createAddressInfoBatch } from ".";
import { createAddressInfo } from ".";
import { addressInfoPresets } from ".";
import { createFinancialInfo } from ".";
import { createFinancialInfoBatch } from ".";
import { financialInfoPresets } from ".";

export const createVirtualAssetData = generateVirtualAssetMockData;
export { calculateVirtualAssetScore };
export const getRandomScore = generateRandomScore;
export { VIRTUAL_ASSET_PRESETS };

export type { VirtualAssetMockConfig as VirtualAssetConfig } from "./virtual-asset-test/mockDataGenerator";

/* ============================================================================
 * 工具函数
 * ========================================================================== */

// 文件上传触发工具
export {
  triggerFileUpload,
  triggerMultipleFileUploads,
  triggerFileUploadByTestId,
  triggerAllFileUploads,
  type TriggerFileUploadOptions,
} from "./utils/mockFileUploadTrigger";

/* ============================================================================
 * Demo Panel 组件（用于开发和演示）
 * ========================================================================== */

// 个人KYC Panel
import { PersonalInfoMockDataPanel } from "./personal/personal-info/mockDataGenerator.demo";
import { AddressInfoMockDataPanel } from "./personal/address-info/mockDataGenerator.demo";
import { FinancialInfoMockDataPanel } from "./personal/financial-info/mockDataGenerator.demo";

export { PersonalInfoMockDataPanel as PersonalInfoPanel };
export { AddressInfoMockDataPanel as AddressInfoPanel };
export { FinancialInfoMockDataPanel as FinancialInfoPanel };

// 企业KYC Panel
import { CompanyMockDataPanel } from "./company/company-info/mockDataGenerator.demo";
import { CompanyStructureMockDataPanel } from "./company/company-structure/mockDataGenerator.demo";
import { CompanyFinancialMockDataPanel } from "./company/company-financial/mockDataGenerator.demo";
import { CompanyUploadFileMockDataPanel } from "./company/company-upload-file/mockDataGenerator.demo";

export { CompanyMockDataPanel as CompanyInfoPanel };
export { CompanyStructureMockDataPanel as CompanyStructurePanel };
export { CompanyFinancialMockDataPanel as CompanyFinancialPanel };
export { CompanyUploadFileMockDataPanel as CompanyUploadFilePanel };

// 测评问卷 Panel
import { RiskAssessmentMockDataPanel } from "./risk-assessment/mockDataGenerator.demo";
import { VirtualAssetMockDataPanel } from "./virtual-asset-test/mockDataGenerator.demo";

export { RiskAssessmentMockDataPanel as RiskAssessmentPanel };
export { VirtualAssetMockDataPanel as VirtualAssetPanel };

// 其他 Panel
import { EnhancedDueDiligenceMockDataPanel } from "./enhanced-due-diligence/mockDataGenerator.demo";
import { SupplementaryMaterialsMockDataPanel } from "./supplementary-materials/mockDataGenerator.demo";

export { EnhancedDueDiligenceMockDataPanel as EnhancedDueDiligencePanel };
export { SupplementaryMaterialsMockDataPanel as SupplementaryMaterialsPanel };

/* ============================================================================
 * 便捷导出
 * ========================================================================== */

/**
 * 所有个人KYC相关的生成器
 */
export const PersonalKYC = {
  // 个人信息
  personalInfo: {
    create: generatePersonalInfoMockData,
    createBatch: generatePersonalInfoMockDataBatch,
    presets: personalInfoPresets,
  },
  // 地址信息
  address: {
    create: createAddressInfo,
    createBatch: createAddressInfoBatch,
    presets: addressInfoPresets,
  },
  // 财务信息
  financial: {
    create: createFinancialInfo,
    createBatch: createFinancialInfoBatch,
    presets: financialInfoPresets,
  },
} as const;

/**
 * 所有企业KYC相关的生成器
 */
export const CompanyKYC = {
  // 公司信息
  companyInfo: {
    create: createCompanyInfo,
    createBatch: createCompanyInfoBatch,
    presets: companyInfoPresets,
  },
  // 公司结构
  structure: {
    create: createRelatedPerson,
    createBatch: createRelatedPersonBatch,
    presets: companyStructurePresets,
  },
  // 公司财务
  financial: {
    create: createCompanyFinancial,
    createBatch: createCompanyFinancialBatch,
    presets: companyFinancialPresets,
  },
} as const;

/**
 * 测评问卷相关的生成器
 */
export const Assessment = {
  // 风险测评
  risk: {
    create: createRiskAssessment,
    presets: RISK_ASSESSMENT_PRESETS,
  },
  // 虚拟资产测试
  virtualAsset: {
    create: createVirtualAssetData,
    calculateScore: calculateVirtualAssetScore,
    presets: VIRTUAL_ASSET_PRESETS,
  },
} as const;

/**
 * 工具函数集合
 */
export const Utils = {
  fileUpload: {
    trigger: triggerFileUpload,
    triggerMultiple: triggerMultipleFileUploads,
    triggerByTestId: triggerFileUploadByTestId,
    triggerAll: triggerAllFileUploads,
  },
} as const;

/**
 * Demo Panel 组件集合（用于开发和演示）
 */
export const Panels = {
  // 个人KYC
  PersonalInfo: PersonalInfoMockDataPanel,
  AddressInfo: AddressInfoMockDataPanel,
  FinancialInfo: FinancialInfoMockDataPanel,
  // 企业KYC
  CompanyInfo: CompanyMockDataPanel,
  CompanyStructure: CompanyStructureMockDataPanel,
  CompanyFinancial: CompanyFinancialMockDataPanel,
  CompanyUploadFile: CompanyUploadFileMockDataPanel,
  // 测评问卷
  RiskAssessment: RiskAssessmentMockDataPanel,
  VirtualAsset: VirtualAssetMockDataPanel,
  // 其他
  EnhancedDueDiligence: EnhancedDueDiligenceMockDataPanel,
  SupplementaryMaterials: SupplementaryMaterialsMockDataPanel,
} as const;

/**
 * 默认导出：包含所有模块的便捷访问
 */
export default {
  PersonalKYC,
  CompanyKYC,
  Assessment,
  Utils,
  Panels,
} as const;
